import { useEffect, useMemo, useState } from 'react';

import { ChevronLeft, ChevronRight, OpenInNew } from '@mui/icons-material';
import { Divider } from '@mui/material';
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Pagination,
  Tab,
  Tabs,
  Theme,
  Typography,
  useMediaQuery,
} from '@mui/material';
import kebabcase from 'lodash/kebabCase';
import { useQuery } from 'react-query';
import { useLocation, useParams } from 'react-router-dom';
import { useEmbedContext } from 'src/context/EmbedContextProvider';
import { findRanking } from 'src/services/ranking';
import { getFiagroRankings } from 'src/services/ranking/fiagroRanking';
import { convertValue } from 'src/utils/number';

import { YearbookParams } from '../..';
import { GradientCircle } from '../../components/GradientCircle';
import { ShareButton } from '../../components/ShareButton';
import { colors, sectionTitleGradient } from '../../constants';
import {
  extractRankingParams,
  isMetabaseRankingUrl,
  isPlatformRankingUrl,
} from '../../utils/rankings';

interface RankingRawRow {
  position: number;
  name: string;
  value: number | string;
  percentage?: number;
  slug?: string;
  institutionId?: number | null;
  operationId?: number | null;
  uqbarId?: number | null;
  logo?: string;
}

interface RankingItem {
  title: string;
  link?: string | null;
  pending?: boolean;
  description?: string | null;
  data?: RankingRawRow[];
  valueLabel?: string;
}

interface RankingsData {
  intro: string[];
  items: RankingItem[];
}

interface RankingsSectionProps {
  id: string;
  data: RankingsData;
}

const title = 'Rankings';

function ModernRankingTable({
  rows,
  valueLabel,
  operationMarket,
  isPlatformRows,
  nameColumnLabel,
}: {
  rows: RankingRawRow[];
  valueLabel?: string;
  operationMarket?: string;
  isPlatformRows: boolean;
  nameColumnLabel: string;
}) {
  const isMonetary = valueLabel?.toLowerCase().includes('montante');

  function getInitials(name: string) {
    return name
      .split(' ')
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part[0]?.toUpperCase() ?? '')
      .join('');
  }

  function getRowHref(row: RankingRawRow) {
    if (!isPlatformRows || !operationMarket) {
      return null;
    }

    if (row.operationId && row.slug && row.uqbarId) {
      return `/operacao/${operationMarket.toLowerCase()}/${row.slug}/${row.uqbarId}`;
    }

    if (row.institutionId && row.slug) {
      return `/instituicao/${row.slug}/${row.institutionId}`;
    }

    return null;
  }

  return (
    <Box
      sx={{
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'rgba(255,255,255,0.08)',
        overflow: 'hidden',
      }}>
      <Box
        sx={{
          width: '100%',
          overflowX: 'auto',
          overflowY: 'hidden',
          scrollbarWidth: 'thin',
        }}>
        <Box sx={{ minWidth: 620 }}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: '64px minmax(230px, 1fr) 120px 160px',
              bgcolor: 'rgba(255,255,255,0.04)',
              px: 2,
              py: 1.25,
              gap: 1,
            }}>
            <Typography
              fontSize="0.72rem"
              fontWeight={700}
              color={colors.textDim}>
              POS
            </Typography>
            <Typography
              fontSize="0.72rem"
              fontWeight={700}
              color={colors.textDim}>
              {nameColumnLabel.toUpperCase()}
            </Typography>
            <Typography
              fontSize="0.72rem"
              fontWeight={700}
              color={colors.textDim}
              textAlign="right">
              %
            </Typography>
            <Typography
              fontSize="0.72rem"
              fontWeight={700}
              color={colors.textDim}
              textAlign="right">
              VALOR
            </Typography>
          </Box>

          {rows.map((row) =>
            // Ranking endpoints may return numeric values as strings.
            // Normalize for consistent formatting in the table.
            (() => {
              const rowHref = getRowHref(row);
              const valueNumber =
                typeof row.value === 'number'
                  ? row.value
                  : Number(row.value ?? 0);
              const percentageNumber =
                typeof row.percentage === 'number'
                  ? row.percentage
                  : Number(row.percentage ?? NaN);

              return (
                <Box
                  key={`${row.position}-${row.name}`}
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: '64px minmax(230px, 1fr) 120px 160px',
                    px: 2,
                    py: 1.25,
                    gap: 1,
                    borderTop: '1px solid',
                    borderColor: 'rgba(255,255,255,0.06)',
                    '&:hover': {
                      bgcolor: 'rgba(255,130,17,0.04)',
                    },
                  }}>
                  <Typography color="white" fontWeight={700} fontSize="0.85rem">
                    {row.position}
                  </Typography>
                  <Box
                    sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                    <Box
                      sx={{
                        width: 30,
                        height: 30,
                        flexShrink: 0,
                        borderRadius: '50%',
                        bgcolor: 'white',
                        border: '1px solid rgba(255,255,255,0.1)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        overflow: 'hidden',
                      }}>
                      {row.logo ? (
                        <Box
                          component="img"
                          src={row.logo}
                          alt={row.name}
                          sx={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                          }}
                        />
                      ) : (
                        <Typography
                          color={colors.textDim}
                          fontWeight={700}
                          fontSize="0.62rem"
                          lineHeight={1}>
                          {getInitials(row.name)}
                        </Typography>
                      )}
                    </Box>

                    {rowHref ? (
                      <Box
                        component="a"
                        href={rowHref}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{
                          color: 'white',
                          fontWeight: 700,
                          fontSize: '0.85rem',
                          textDecoration: 'none',
                          '&:hover': {
                            textDecoration: 'underline',
                            textDecorationColor: 'primary.main',
                          },
                        }}>
                        {row.name}
                      </Box>
                    ) : (
                      <Typography
                        color="white"
                        fontWeight={600}
                        fontSize="0.85rem">
                        {row.name}
                      </Typography>
                    )}
                  </Box>
                  <Typography
                    color={colors.text}
                    textAlign="right"
                    fontSize="0.85rem">
                    {Number.isFinite(percentageNumber)
                      ? `${percentageNumber.toLocaleString('pt-BR', {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}%`
                      : '-'}
                  </Typography>
                  <Typography
                    color={colors.text}
                    textAlign="right"
                    fontSize="0.85rem">
                    {isMonetary
                      ? convertValue(valueNumber, undefined, true, 'currency')
                      : valueNumber.toLocaleString('pt-BR', {
                          maximumFractionDigits: 2,
                        })}
                  </Typography>
                </Box>
              );
            })(),
          )}
        </Box>
      </Box>
    </Box>
  );
}

export function RankingsSection({ id, data }: RankingsSectionProps) {
  const location = useLocation();
  const { market } = useParams<YearbookParams>();
  const { widgetKey } = useEmbedContext();
  const [selectedTab, setSelectedTab] = useState(0);
  const [page, setPage] = useState(1);
  const isMobile = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down('md'),
  );

  const selectedRankingFromQuery = useMemo(() => {
    return new URLSearchParams(location.search).get('ranking');
  }, [location.search]);

  const hasItems = data.items.length > 0;
  const activeRanking = data.items[selectedTab] ?? {
    title: '',
    link: null,
    pending: true,
    description: null,
    data: [],
  };
  const rankingParams = extractRankingParams(activeRanking?.link);
  const isPlatformLink = isPlatformRankingUrl(activeRanking?.link);
  const isMetabaseLink = isMetabaseRankingUrl(activeRanking?.link);
  const operationMarket = useMemo(() => {
    const fromRankingLink = rankingParams?.market?.toLowerCase();

    if (fromRankingLink === 'crs') {
      return 'cr';
    }

    return fromRankingLink ?? market?.toLowerCase();
  }, [market, rankingParams?.market]);

  const isFiagroMarket = operationMarket === 'fiagro';

  const { data: platformRanking, isLoading } = useQuery(
    ['yearbook-2026-ranking', activeRanking?.link],
    () =>
      isFiagroMarket
        ? getFiagroRankings({
            type: rankingParams?.ranking,
            criteria: rankingParams?.criteria,
            year: rankingParams?.year,
            timeRange: 'consolidated',
            isLoggedIn: false,
          })
        : findRanking({
            filters: {
              market: rankingParams?.market?.toLowerCase(),
              year: rankingParams?.year,
              type: rankingParams?.ranking,
              criteria: rankingParams?.criteria,
              timeRange: rankingParams?.timeRange,
            },
            isLoggedIn: true,
            widgetKey,
          }),
    {
      enabled: Boolean(isPlatformLink && rankingParams),
    },
  );

  const rows = useMemo(() => {
    if (isPlatformLink) {
      return (platformRanking?.ranks ?? []) as RankingRawRow[];
    }

    return (activeRanking?.data ?? []) as RankingRawRow[];
  }, [activeRanking?.data, isPlatformLink, platformRanking?.ranks]);

  const pageSize = 10;
  const pageCount = Math.ceil(rows.length / pageSize);
  const pageStart = (page - 1) * pageSize;
  const paginatedRows = rows.slice(pageStart, pageStart + pageSize);

  useEffect(() => {
    if (!selectedRankingFromQuery) {
      return;
    }

    const rankingIndex = data.items.findIndex(
      (item) => kebabcase(item.title) === selectedRankingFromQuery,
    );

    if (rankingIndex >= 0) {
      setSelectedTab(rankingIndex);
      setPage(1);
    }
  }, [data.items, selectedRankingFromQuery]);

  useEffect(() => {
    setPage(1);
  }, [selectedTab]);

  useEffect(() => {
    if (page > pageCount && pageCount > 0) {
      setPage(pageCount);
    }
  }, [page, pageCount]);

  return (
    <Box
      component="section"
      id={id}
      display="flex"
      flexDirection="column"
      gap={4}
      mt={10}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          justifyContent: 'center',
        }}>
        <Typography
          component="h2"
          fontSize={{ xs: 28, md: 40 }}
          fontWeight={700}
          lineHeight={1.2}
          sx={{
            backgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundImage: sectionTitleGradient,
          }}>
          {title}
        </Typography>
        <ShareButton market={market} section={kebabcase(title)} />
        <GradientCircle right="0" size={isMobile ? 300 : 500} />
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {data.intro.map((text, idx) => (
          <Typography key={idx} color={colors.text} lineHeight={1.75}>
            {text}
          </Typography>
        ))}
      </Box>

      <Box
        sx={{
          bgcolor: colors.bgSection,
          borderRadius: 3,
          p: { xs: 2, md: 3 },
          display: 'flex',
          flexDirection: 'column',
          gap: 2.5,
        }}>
        {hasItems && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Tabs
              value={selectedTab}
              onChange={(_, value) => setSelectedTab(value)}
              variant="scrollable"
              scrollButtons={false}
              sx={{
                flex: 1,
                minHeight: 'auto',
                '& .MuiTabs-flexContainer': { gap: 1, flexWrap: 'nowrap' },
                '& .MuiTabs-indicator': { display: 'none' },
                '& .MuiTabs-scroller': {
                  overflowX: 'auto !important',
                  overflowY: 'hidden !important',
                  scrollbarWidth: 'none',
                  '&::-webkit-scrollbar': { display: 'none' },
                },
              }}>
              {data.items.map((item, idx) => (
                <Tab
                  key={item.title}
                  label={
                    <Box
                      component="span"
                      sx={{
                        display: 'block',
                        width: '100%',
                        minWidth: 0,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}>
                      {item.title}
                    </Box>
                  }
                  sx={{
                    width: 224,
                    minWidth: '160px !important',
                    maxWidth: 224,
                    overflow: 'hidden',
                    minHeight: 36,
                    py: 0.75,
                    px: 2,
                    borderRadius: 6,
                    fontSize: '0.72rem',
                    fontWeight: 600,
                    textTransform: 'none',
                    whiteSpace: 'nowrap',
                    flexShrink: 0,
                    transition: 'all 0.2s ease-in-out',
                    color: idx === selectedTab ? 'white' : colors.text,
                    bgcolor:
                      idx === selectedTab
                        ? 'primary.main'
                        : 'rgba(255,255,255,0.06)',
                    '&.Mui-selected': {
                      color: 'white',
                      bgcolor: 'primary.main',
                    },
                    '&:hover': {
                      bgcolor:
                        idx === selectedTab
                          ? 'primary.dark'
                          : 'rgba(255,255,255,0.12)',
                    },
                  }}
                />
              ))}
            </Tabs>

            <Box sx={{ display: 'flex', gap: 0.5 }}>
              <IconButton
                size="small"
                aria-label="ranking anterior"
                disabled={selectedTab === 0}
                onClick={() => setSelectedTab((prev) => Math.max(prev - 1, 0))}
                sx={{
                  width: 32,
                  height: 32,
                  bgcolor:
                    selectedTab === 0
                      ? 'rgba(255,255,255,0.06)'
                      : 'primary.main',
                  color: selectedTab === 0 ? 'rgba(255,255,255,0.3)' : 'white',
                  '&:hover': {
                    bgcolor:
                      selectedTab === 0
                        ? 'rgba(255,255,255,0.06)'
                        : 'primary.dark',
                  },
                  '&.Mui-disabled': {
                    bgcolor: 'rgba(255,255,255,0.06)',
                    color: 'rgba(255,255,255,0.25)',
                  },
                }}>
                <ChevronLeft fontSize="small" />
              </IconButton>

              <IconButton
                size="small"
                aria-label="próximo ranking"
                disabled={selectedTab === data.items.length - 1}
                onClick={() =>
                  setSelectedTab((prev) =>
                    Math.min(prev + 1, data.items.length - 1),
                  )
                }
                sx={{
                  width: 32,
                  height: 32,
                  bgcolor:
                    selectedTab === data.items.length - 1
                      ? 'rgba(255,255,255,0.06)'
                      : 'primary.main',
                  color:
                    selectedTab === data.items.length - 1
                      ? 'rgba(255,255,255,0.3)'
                      : 'white',
                  '&:hover': {
                    bgcolor:
                      selectedTab === data.items.length - 1
                        ? 'rgba(255,255,255,0.06)'
                        : 'primary.dark',
                  },
                  '&.Mui-disabled': {
                    bgcolor: 'rgba(255,255,255,0.06)',
                    color: 'rgba(255,255,255,0.25)',
                  },
                }}>
                <ChevronRight fontSize="small" />
              </IconButton>
            </Box>
          </Box>
        )}

        {hasItems && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 1.25,
              mt: 2,
              width: { xs: '100%', md: '80%' },
              mx: 'auto',
            }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                flexWrap: 'wrap',
              }}>
              <Typography
                color="white"
                fontWeight={700}
                textTransform="uppercase">
                &mdash; {activeRanking.title}
              </Typography>
              <ShareButton
                market={market}
                section={`${kebabcase(title)}?ranking=${kebabcase(activeRanking.title)}`}
                size={20}
              />
            </Box>
            {activeRanking.description && (
              <Typography color={colors.text} lineHeight={1.75}>
                {activeRanking.description}
              </Typography>
            )}
            {isPlatformLink && activeRanking.link && (
              <Button
                component="a"
                href={activeRanking.link}
                target="_blank"
                rel="noopener noreferrer"
                size="small"
                variant="outlined"
                endIcon={<OpenInNew sx={{ fontSize: 14 }} />}
                sx={{
                  maxWidth: 240,
                  textTransform: 'none',
                  minHeight: 30,
                  borderRadius: 6,
                  borderColor: 'rgba(255,130,17,0.6)',
                  color: 'white',
                  '&:hover': {
                    borderColor: 'primary.main',
                    bgcolor: 'rgba(255,130,17,0.08)',
                  },
                }}>
                Abrir ranking na plataforma
              </Button>
            )}
          </Box>
        )}
        <Divider
          sx={{
            mt: 2,
            bgcolor: 'rgba(255, 255, 255, 0.1)',
            mx: 8,
            position: 'relative',
            height: 2,
          }}>
          <Box
            sx={{
              position: 'absolute',
              left: 0,
              top: 0,
              bottom: 0,
              width: '50%',
              background: 'linear-gradient(to right, #1A1A1A, transparent)',
              zIndex: 2,
              pointerEvents: 'none',
            }}
          />

          <Box
            sx={{
              position: 'absolute',
              right: 0,
              top: 0,
              bottom: 0,
              width: '50%',
              background: 'linear-gradient(to left, #1A1A1A, transparent)',
              zIndex: 2,
              pointerEvents: 'none',
            }}
          />
        </Divider>

        {!hasItems && (
          <Box
            sx={{
              borderRadius: 2,
              border: '1px dashed rgba(255,255,255,0.16)',
              p: 3,
            }}>
            <Typography color={colors.text}>
              Rankings deste mercado ainda não foram disponibilizados em formato
              estruturado.
            </Typography>
          </Box>
        )}

        {hasItems && activeRanking.pending && (
          <Box
            sx={{
              borderRadius: 2,
              border: '1px dashed rgba(255,255,255,0.16)',
              p: 3,
            }}>
            <Typography color={colors.text}>
              Este ranking ainda está em processamento e será disponibilizado em
              breve.
            </Typography>
          </Box>
        )}

        {hasItems && !activeRanking.pending && isLoading && (
          <Box
            sx={{
              minHeight: 220,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
            <CircularProgress size={28} sx={{ color: 'primary.main' }} />
          </Box>
        )}

        {hasItems &&
          !activeRanking.pending &&
          !isLoading &&
          rows.length > 0 && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <ModernRankingTable
                rows={paginatedRows}
                valueLabel={
                  platformRanking?.valueLabel ?? activeRanking.valueLabel
                }
                operationMarket={operationMarket}
                isPlatformRows={isPlatformLink}
                nameColumnLabel={platformRanking?.label ?? 'Nome'}
              />
              {pageCount > 1 && (
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    color: 'white',
                  }}>
                  <Pagination
                    sx={{
                      '& .MuiPaginationItem-root': {
                        color: 'white',
                      },
                    }}
                    page={page}
                    count={pageCount}
                    onChange={(_, value) => setPage(value)}
                    color="primary"
                    shape="rounded"
                    size="small"
                  />
                </Box>
              )}
            </Box>
          )}

        {hasItems &&
          !activeRanking.pending &&
          !isLoading &&
          rows.length === 0 &&
          isMetabaseLink && (
            <Box
              sx={{
                borderRadius: 2,
                border: '1px dashed rgba(255,255,255,0.16)',
                p: 3,
              }}>
              <Typography color={colors.text}>
                Este ranking possui origem Metabase e está sem dados puros
                carregados no anuário 2026.
              </Typography>
            </Box>
          )}

        {hasItems &&
          !activeRanking.pending &&
          !isLoading &&
          rows.length === 0 &&
          !isMetabaseLink && (
            <Box
              sx={{
                borderRadius: 2,
                border: '1px dashed rgba(255,255,255,0.16)',
                p: 3,
              }}>
              <Typography color={colors.text}>
                Não há dados para este ranking.
              </Typography>
            </Box>
          )}
      </Box>
    </Box>
  );
}
